import { Autocomplete, TextField } from '@mui/material'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import I18n from 'i18n-js'
import PropTypes from 'prop-types'
import { useState, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { makeStyles } from 'tss-react/mui'

import LinkChild from '@src/lib/LinkChild'
import MenuItemWithCheckbox from '@src/lib/MenuItemWithCheckbox'
import { selectBrand } from '@src/stores/app/actions'
import { currentRetailerAccountAndBrandSlugSelector } from '@src/stores/app/selectors'

import colors from '../../../lib/Colors'

const useStyles = makeStyles()(() => ({
  root: {
    width: '100%',
    // minWidth: '120px',
    fontFamily: 'Lato, Helvetica',
  },
  select: {
    marginRight: '32px',
    backgroundColor: colors.white,
  },
}))

const MenuItems = ({ items }) => {
  return items.map((item) => {
    const { label, value } = item
    return (
      <MenuItemWithCheckbox key={`menu-item-${value}`} value={`${value}`}>
        {label}
      </MenuItemWithCheckbox>
    )
  })
}

const Filter = ({ items, label, multiSelect, onChange, autoComplete }) => {
  const { brandSlug } = useParams()
  const { classes } = useStyles()
  const [selectedValue, setSelectedValue] = useState(multiSelect ? [0] : 0)
  const inputLabel = useRef(null)
  const [labelWidth, setLabelWidth] = useState(0)
  const currentSlugs = useSelector(currentRetailerAccountAndBrandSlugSelector)
  const dispatch = useDispatch()

  function replaceBrandSlug(newBrandSlug) {
    const current = `${window.location.pathname}${window.location.search}`

    return current.replace(`/${currentSlugs.brand}`, `/${newBrandSlug}`)
  }

  useEffect(() => {
    setLabelWidth((inputLabel.current && inputLabel.current.offsetWidth) || 0)
  }, [])

  useEffect(() => {
    setSelectedValue(multiSelect ? [0] : 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandSlug])

  const onChangeHandler = (event, value) => {
    const valueAdded = autoComplete ? value.value : value.props.value
    let newValue = event.target.value

    if (valueAdded === 0) {
      if (multiSelect) {
        newValue = [0]
      }
    } else {
      if (multiSelect) {
        newValue = event.target.value.filter((x) => x !== 0)
      }
    }

    let valueToEmit = newValue
    if (multiSelect) {
      if (valueToEmit.length === 1 && valueToEmit[0] === 0) {
        valueToEmit = []
      }
    }

    setSelectedValue(newValue)
    onChange(valueToEmit)
  }

  return (
    <FormControl className={classes.root}>
      {autoComplete ? (
        <Autocomplete
          // multiple << Keeping this here as it will be added in phase 2
          options={items}
          value={items.find((item) => item.slug === brandSlug) || items[0]}
          disableClearable
          renderOption={(props, option) => (
            // This works for phase 1 because we only want to select one brand at a time
            // In phase 2 we will need to add multiSelect, and add an onChange handler instead of rendering LinkChilds
            <LinkChild
              to={replaceBrandSlug(option.slug)}
              onClick={() => {
                dispatch(
                  selectBrand({
                    id: option.id ? Number(option.id) : null,
                    skipUrlUpdate: true,
                  }),
                )
              }}
              key={option.slug}
            >
              <MenuItemWithCheckbox
                {...props}
                value={option.value}
                selected={option.slug === brandSlug}
              >
                {option.label}
              </MenuItemWithCheckbox>
            </LinkChild>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              labelId={label}
              labelWidth={labelWidth}
              className={classes.select}
            />
          )}
        />
      ) : (
        <>
          <InputLabel id={label} ref={inputLabel}>
            {label}
          </InputLabel>
          <Select
            className={classes.select}
            label={label}
            labelId={label}
            labelWidth={labelWidth}
            multiple={multiSelect}
            onChange={onChangeHandler}
            value={selectedValue}
          >
            <MenuItemWithCheckbox value={0}>
              {I18n.t('filters.all')}
            </MenuItemWithCheckbox>
            {MenuItems({ items })}
          </Select>
        </>
      )}
    </FormControl>
  )
}

Filter.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ),
  label: PropTypes.string,
  multiSelect: PropTypes.bool,
  onChange: PropTypes.func,
  selectWidth: PropTypes.string,
  autoComplete: PropTypes.bool,
}

Filter.defaultProps = {
  label: 'Filter by:',
  multiSelect: false,
  selectWidth: '110px',
}

export default Filter
